import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Row, Col, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import './PaymentTracking.css';

const ProjectPaymentDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [project, setProject] = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    paymentType: 'check',
    paymentTypeOther: '',
    checkNumber: '',
    confirmationNumber: '',
    notes: ''
  });

  useEffect(() => {
    fetchPaymentDetails();
  }, [projectId]);

  const fetchPaymentDetails = async () => {
    try {
      const response = await fetch(`/api/payment-tracking/project/${projectId}`, {
        headers: {
          'x-auth-token': token
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment details');
      }

      const data = await response.json();
      setProject(data.project);
      setPayments(data.payments);
      
      // Debug logging
      console.log('Payments received:', data.payments);
      data.payments.forEach(p => {
        console.log(`Payment: ${p.Description}, Status: "${p.Status}" (type: ${typeof p.Status})`);
      });

      // Fetch summary
      const summaryResponse = await fetch(`/api/payment-tracking/project/${projectId}/summary`, {
        headers: {
          'x-auth-token': token
        }
      });

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setSummary(summaryData);
      }
    } catch (err) {
      console.error('Error fetching payment details:', err);
      toast.error('Failed to load payment details');
      navigate('/payments');
    } finally {
      setIsLoading(false);
    }
  };

  const openReceiveModal = (payment) => {
    setSelectedPayment(payment);
    setIsEditing(false);
    setFormData({
      paymentDate: new Date().toISOString().split('T')[0],
      paymentType: 'check',
      paymentTypeOther: '',
      checkNumber: '',
      confirmationNumber: '',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (payment) => {
    setSelectedPayment(payment);
    setIsEditing(true);
    setFormData({
      paymentDate: payment.PaymentDate ? new Date(payment.PaymentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      paymentType: payment.PaymentType || 'check',
      paymentTypeOther: payment.PaymentTypeOther || '',
      checkNumber: payment.CheckNumber || '',
      confirmationNumber: payment.ConfirmationNumber || '',
      notes: payment.ReceivedNotes || ''
    });
    setShowModal(true);
  };

  const handleUndoPayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to undo this payment? It will be marked as pending again.')) {
      return;
    }

    try {
      const response = await fetch(`/api/payment-tracking/payment/${paymentId}/undo`, {
        method: 'PUT',
        headers: {
          'x-auth-token': token
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to undo payment');
      }

      toast.success('Payment marked as pending');
      fetchPaymentDetails(); // Refresh the data
    } catch (err) {
      console.error('Error undoing payment:', err);
      toast.error(err.message || 'Failed to undo payment');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMarkReceived = async (e) => {
    e.preventDefault();

    try {
      const endpoint = isEditing 
        ? `/api/payment-tracking/payment/${selectedPayment.PaymentItemId}/update`
        : `/api/payment-tracking/payment/${selectedPayment.PaymentItemId}/receive`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isEditing ? 'update' : 'mark'} payment`);
      }

      toast.success(isEditing ? 'Payment updated successfully' : 'Payment marked as received successfully');
      setShowModal(false);
      fetchPaymentDetails(); // Refresh the data
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'marking'} payment:`, err);
      toast.error(err.message || `Failed to ${isEditing ? 'update' : 'mark'} payment`);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return <div className="text-center mt-5">Loading payment details...</div>;
  }

  return (
    <div className="payment-details-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/payments')} className="mb-2">
            ← Back to Projects
          </Button>
          <h2>{project?.ProjectName}</h2>
          <p className="text-muted mb-0">{project?.ClientName}</p>
        </div>
      </div>

      {/* Summary Card */}
      {summary && (
        <Card className="summary-card mb-4">
          <Card.Body>
            <Row>
              <Col md={3}>
                <div className="summary-item">
                  <div className="summary-label">Total Payments</div>
                  <div className="summary-value">{summary.TotalPayments || 0}</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="summary-item">
                  <div className="summary-label">Received</div>
                  <div className="summary-value">{summary.ReceivedCount || 0}</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="summary-item">
                  <div className="summary-label">Amount Received</div>
                  <div className="summary-value">{formatCurrency(summary.ReceivedAmount)}</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="summary-item">
                  <div className="summary-label">Amount Pending</div>
                  <div className="summary-value">{formatCurrency(summary.PendingAmount)}</div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Payments List */}
      <div>
        {payments.length > 0 ? (
          payments.map((payment, index) => (
            <div 
              key={payment.PaymentItemId} 
              className={`payment-item ${payment.Status === 'received' ? 'received' : 'pending'}`}
            >
              <div className="payment-header">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h5 className="mb-0">
                      {payment.Description || `Payment #${payment.PaymentOrder}`}
                    </h5>
                    {payment.IsChangeOrder && (
                      <Badge bg="info">Change Order</Badge>
                    )}
                    {payment.Status === 'received' ? (
                      <Badge bg="success">Received</Badge>
                    ) : (
                      <Badge bg="warning" text="dark">Pending</Badge>
                    )}
                  </div>
                  {payment.IsChangeOrder && payment.ChangeOrderDescription && (
                    <small className="text-muted d-block mb-1" style={{ fontStyle: 'italic' }}>
                      {payment.ChangeOrderDescription}
                    </small>
                  )}
                  <div className="payment-amount text-primary">
                    {formatCurrency(payment.Amount)}
                  </div>
                  {payment.DueDate && (
                    <small className="text-muted">Due: {formatDate(payment.DueDate)}</small>
                  )}
                </div>
                <div>
                  {!payment.Status || payment.Status === 'pending' ? (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => openReceiveModal(payment)}
                    >
                      Mark as Received
                    </Button>
                  ) : payment.Status === 'received' ? (
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openEditModal(payment)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleUndoPayment(payment.PaymentItemId)}
                      >
                        Undo
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => openReceiveModal(payment)}
                    >
                      Mark as Received
                    </Button>
                  )}
                </div>
              </div>

              {payment.Status === 'received' && (
                <div className="payment-details">
                  <div className="payment-detail-item">
                    <div className="payment-detail-label">Payment Date</div>
                    <div className="payment-detail-value">{formatDate(payment.PaymentDate)}</div>
                  </div>
                  <div className="payment-detail-item">
                    <div className="payment-detail-label">Payment Method</div>
                    <div className="payment-detail-value">
                      {payment.PaymentType === 'other' && payment.PaymentTypeOther
                        ? payment.PaymentTypeOther
                        : payment.PaymentType ? payment.PaymentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'
                      }
                    </div>
                  </div>
                  {payment.CheckNumber && (
                    <div className="payment-detail-item">
                      <div className="payment-detail-label">Check Number</div>
                      <div className="payment-detail-value">{payment.CheckNumber}</div>
                    </div>
                  )}
                  {payment.ConfirmationNumber && (
                    <div className="payment-detail-item">
                      <div className="payment-detail-label">Confirmation #</div>
                      <div className="payment-detail-value">{payment.ConfirmationNumber}</div>
                    </div>
                  )}
                  {payment.ReceivedNotes && (
                    <div className="payment-detail-item" style={{ flexBasis: '100%' }}>
                      <div className="payment-detail-label">Notes</div>
                      <div className="payment-detail-value">{payment.ReceivedNotes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <Alert variant="info">
            No payment schedule found for this project.
          </Alert>
        )}
      </div>

      {/* Mark Payment Received Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Edit Payment Details' : 'Mark Payment as Received'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleMarkReceived}>
          <Modal.Body>
            {selectedPayment && (
              <div className="mb-3">
                <h5>{selectedPayment.Description || `Payment #${selectedPayment.PaymentOrder}`}</h5>
                <h4 className="text-primary">{formatCurrency(selectedPayment.Amount)}</h4>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Payment Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleFormChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Payment Type <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="paymentType"
                value={formData.paymentType}
                onChange={handleFormChange}
                required
              >
                <option value="check">Check</option>
                <option value="credit_card">Credit Card</option>
                <option value="cash">Cash</option>
                <option value="wire_transfer">Wire Transfer</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>

            {formData.paymentType === 'other' && (
              <Form.Group className="mb-3">
                <Form.Label>Please Specify <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="paymentTypeOther"
                  value={formData.paymentTypeOther}
                  onChange={handleFormChange}
                  placeholder="Enter payment type"
                  required
                />
              </Form.Group>
            )}

            {formData.paymentType === 'check' && (
              <Form.Group className="mb-3">
                <Form.Label>Check Number</Form.Label>
                <Form.Control
                  type="text"
                  name="checkNumber"
                  value={formData.checkNumber}
                  onChange={handleFormChange}
                  placeholder="Enter check number"
                />
              </Form.Group>
            )}

            {(formData.paymentType === 'credit_card' || formData.paymentType === 'wire_transfer') && (
              <Form.Group className="mb-3">
                <Form.Label>Confirmation Number</Form.Label>
                <Form.Control
                  type="text"
                  name="confirmationNumber"
                  value={formData.confirmationNumber}
                  onChange={handleFormChange}
                  placeholder="Enter confirmation number"
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Any additional notes about this payment..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit">
              {isEditing ? 'Update Payment' : 'Confirm Receipt'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectPaymentDetails;
